/**
 * Client-side Firestore API for the `dunning_policies`, `dunning_events`,
 * `dunning_invoice_settings` (MA-3), and `dunning_level_locks` (MA-3, R3/E11)
 * collections.
 *
 * All reads and writes are owner-scoped (M0-Konform):
 *   - Every write sets `userId = auth.currentUser.uid`.
 *   - Every read query carries `where('userId', '==', uid)`.
 *
 * `dunning_events` are APPEND-ONLY: no update or delete functions are exposed.
 * This matches the Firestore Rules (`update, delete: if false`) and the GoBD
 * consideration that a generated dunning notice is a record of a business action
 * (architecture-m2.md §1.2). `dunning_level_locks` is likewise APPEND-ONLY
 * (create-only — see {@link acquireDunningLevelLock}); `dunning_invoice_settings`
 * is a normal mutable owner-scoped collection (architecture-mahn-automatik.md §6.5).
 *
 * Cent-Disziplin: callers must pass all monetary values as integer cents.
 * Use `eurosToCents()` from `utils/moneyUtils.ts` at UI input boundaries.
 *
 * Feature-Flag: This module is inert until M2c activates the dunning UI behind
 *   `process.env.REACT_APP_DUNNING_ENABLED === 'true'` (fail-closed, default OFF).
 *   The MA-3 additions (`automation`, `dunning_invoice_settings`,
 *   `dunning_level_locks`) are themselves inert until MA-6 wires them into the
 *   UI (`REACT_APP_DUNNING_SEMIAUTO_ENABLED` / `REACT_APP_DUNNING_AUTO_ENABLED`).
 *
 * Index requirements (must exist in Firestore before production use):
 *   - `dunning_events`: `userId ASC, invoiceId ASC`  (for getDunningEventsForInvoice)
 *   - `dunning_events`: `userId ASC, createdAt DESC` (for potential future list views)
 *   - `dunning_invoice_settings`: single-field auto-index on `userId` suffices
 *     (getDunningInvoiceSettingsForUser uses no `orderBy`).
 *   - `dunning_level_locks`: no query index needed (docId is deterministic,
 *     never queried by field).
 *
 * @module dunningApi
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../firebaseConfig';
import { getEffectiveScopeUid } from '../utils/tenantScope';
import {
  DunningPolicy,
  DunningEvent,
  CreateDunningEventInput,
  DunningDelivery,
  DunningInvoiceSettings,
  DunningLevelLock,
} from '../types/dunningTypes';

const DUNNING_POLICIES_COLLECTION = 'dunning_policies';
const DUNNING_EVENTS_COLLECTION = 'dunning_events';
const DUNNING_DELIVERIES_COLLECTION = 'dunning_deliveries';
const DUNNING_INVOICE_SETTINGS_COLLECTION = 'dunning_invoice_settings';
const DUNNING_LEVEL_LOCKS_COLLECTION = 'dunning_level_locks';

// ─── Auth guard ───────────────────────────────────────────────────────────────

/**
 * Returns the effective scope UID for owner-scoped queries.
 *
 * Fail-closed: throws if no user is signed in (Golden Rule — never an anonymous
 * scope). The returned value is the *active tenant scope* via
 * {@link getEffectiveScopeUid}: bit-identical to `auth.currentUser.uid` while the
 * flag is off or the own tenant is active (M11 inertness), and the mandate's
 * `scopeUid` when an advisor has a foreign tenant active.
 */
function requireCurrentUser(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error(
      'dunningApi: No authenticated user. Sign in before accessing dunning data.',
    );
  }
  return getEffectiveScopeUid();
}

// ─── dunning_policies ─────────────────────────────────────────────────────────

/**
 * Loads the dunning policy for a specific user.
 *
 * The policy document ID equals the userId (1:1 relationship, architecture-m2.md §1.1).
 * Scoped: only the owner can read their own policy (Firestore Rules enforce this).
 *
 * @param uid - UID of the user whose policy to load. Must match `auth.currentUser.uid`.
 * @returns The user's DunningPolicy, or null if no policy has been configured yet.
 * @throws {Error} If not authenticated, cross-user read attempted, or Firestore fails.
 */
export async function getDunningPolicy(uid: string): Promise<DunningPolicy | null> {
  const currentUid = requireCurrentUser();

  if (uid !== currentUid) {
    throw new Error(
      'dunningApi.getDunningPolicy: uid must match the authenticated user. ' +
      'Cross-user reads are denied by Firestore Rules.',
    );
  }

  const docRef = doc(db, DUNNING_POLICIES_COLLECTION, uid);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return { id: snapshot.id, ...snapshot.data() } as DunningPolicy;
}

/**
 * Creates or fully replaces the dunning policy for the authenticated user.
 *
 * Uses `setDoc` (upsert semantics) because there is exactly one policy per user
 * and the document ID equals the userId — a predictable, stable key.
 *
 * Enforces:
 *   - `userId` is always set to `auth.currentUser.uid` (M0 scope).
 *   - `autoSendMode` is hard-coded to 'manual_approve' (M2 constraint;
 *     'auto' is reserved for M6 — architecture-m2.md §1.1). This deprecated
 *     alias is UNRELATED to `automation.mode` (MA-3, E6) and is never derived
 *     from it.
 *   - `automation` (MA-3, additive-optional) is passed through unchanged when
 *     present — this function does NOT clamp it. Every reader MUST run it
 *     through `resolveAutomationSettings()` (R2, fail-closed hard clamps);
 *     this write path intentionally persists the raw, caller-supplied shape.
 *   - `updatedAt` is set via `serverTimestamp()`.
 *
 * @param policy - Policy to save. `userId` and `autoSendMode` fields in the
 *                 input are ignored; this function always sets them correctly.
 * @throws {Error} If not authenticated or if the Firestore write fails.
 */
export async function upsertDunningPolicy(
  policy: Omit<DunningPolicy, 'userId' | 'autoSendMode' | 'id' | 'updatedAt'>,
): Promise<void> {
  const uid = requireCurrentUser();

  const docData: Omit<DunningPolicy, 'id'> = {
    userId: uid,
    levels: policy.levels,
    autoSendMode: 'manual_approve', // M2: hard-coded; 'auto' = M6
    updatedAt: serverTimestamp() as DunningPolicy['updatedAt'],
    // MA-3: automation is additive-optional — only written when the caller
    // supplies it, so pre-MA-3 callers keep writing byte-identical docData.
    ...(policy.automation !== undefined ? { automation: policy.automation } : {}),
  };

  const docRef = doc(db, DUNNING_POLICIES_COLLECTION, uid);
  await setDoc(docRef, docData);
}

// ─── dunning_events ───────────────────────────────────────────────────────────

/**
 * Creates a new dunning event document (APPEND-ONLY).
 *
 * Records a dunning notice that was generated for an invoice. Enforces:
 *   - `userId` is always set to `auth.currentUser.uid` (M0 scope).
 *   - `createdBy` is set to the current user's UID.
 *   - `createdAt` and `sentAt` are set via `serverTimestamp()`.
 *   - `invoiceId` must be provided and non-empty.
 *
 * NO update or delete functions exist for this collection — events are immutable
 * once written. Corrections require creating a new event (append-only, GoBD).
 *
 * @param input - Dunning event fields. `userId`, `createdBy`, `createdAt`,
 *                and `sentAt` are set automatically from auth context.
 * @returns Firestore document ID of the newly created event.
 * @throws {Error} If not authenticated, invoiceId is missing, or Firestore fails.
 */
export async function createDunningEvent(
  input: CreateDunningEventInput,
): Promise<string> {
  const uid = requireCurrentUser();

  if (!input.invoiceId || input.invoiceId.trim() === '') {
    throw new Error('dunningApi.createDunningEvent: invoiceId must be a non-empty string.');
  }

  if (!Number.isInteger(input.basisAmountCents) || input.basisAmountCents < 0) {
    throw new Error(
      `dunningApi.createDunningEvent: basisAmountCents must be a non-negative integer ` +
      `(got ${input.basisAmountCents}). Use eurosToCents() from moneyUtils.ts.`,
    );
  }

  if (!Number.isInteger(input.feeCents) || input.feeCents < 0) {
    throw new Error(
      `dunningApi.createDunningEvent: feeCents must be a non-negative integer ` +
      `(got ${input.feeCents}).`,
    );
  }

  if (!Number.isInteger(input.interestCents) || input.interestCents < 0) {
    throw new Error(
      `dunningApi.createDunningEvent: interestCents must be a non-negative integer ` +
      `(got ${input.interestCents}).`,
    );
  }

  const docData: Omit<DunningEvent, 'id'> = {
    userId: uid,
    invoiceId: input.invoiceId,
    level: input.level,
    sentAt: serverTimestamp() as DunningEvent['sentAt'],
    channel: input.channel,
    basisAmountCents: input.basisAmountCents,
    interestFromDateISO: input.interestFromDateISO,
    interestToDateISO: input.interestToDateISO,
    interestCents: input.interestCents,
    feeCents: input.feeCents,
    amountDueCents: input.amountDueCents,
    debtorType: input.debtorType,
    b2cHinweisVorhanden: input.b2cHinweisVorhanden,
    createdAt: serverTimestamp() as DunningEvent['createdAt'],
    createdBy: uid,
    // AP3-FE: pdfStoragePath is written once at creation when the PDF was persisted
    // to Firebase Storage (X-Dunning-Pdf-Path response header).  Until AP3 is live,
    // callers omit this field.
    ...(input.pdfStoragePath !== undefined
      ? { pdfStoragePath: input.pdfStoragePath }
      : {}),
    // AP3-FE: payment deadline printed on the PDF (generation date + 14 days).
    // Used by sendDunningEmail Cloud Function to populate the e-mail body accurately.
    ...(input.newDueDateISO !== undefined
      ? { newDueDateISO: input.newDueDateISO }
      : {}),
    // MA-3: Mahn-Automatik audit fields (architecture-mahn-automatik.md §6.2/§15).
    // 🔴 MUST be copied explicitly like the fields above — a field NOT listed
    // here is silently dropped by Firestore, never written, never an error.
    // `?? undefined` is FORBIDDEN here (Firestore rejects explicit `undefined`
    // values at write time) — each field is included only when the caller
    // actually supplied it.
    ...(input.trigger !== undefined ? { trigger: input.trigger } : {}),
    ...(input.batchId !== undefined ? { batchId: input.batchId } : {}),
    ...(input.approvedBy !== undefined ? { approvedBy: input.approvedBy } : {}),
    ...(input.approvedAtISO !== undefined
      ? { approvedAtISO: input.approvedAtISO }
      : {}),
    ...(input.coverageSnapshot !== undefined
      ? { coverageSnapshot: input.coverageSnapshot }
      : {}),
  };

  const colRef = collection(db, DUNNING_EVENTS_COLLECTION);
  const docRef = await addDoc(colRef, docData);
  return docRef.id;
}

/**
 * Loads all dunning events for a specific invoice, scoped to the current user.
 *
 * Double-scoped: both `userId` and `invoiceId` are required in the query to
 * prevent cross-tenant access even if Firestore Rules were misconfigured.
 *
 * Requires a composite Firestore index: `userId ASC, invoiceId ASC`.
 *
 * @param invoiceId - Firestore document ID of the invoice.
 * @returns Array of DunningEvent objects ordered by `createdAt` ascending
 *          (chronological notice history). May be empty if no notices exist yet.
 * @throws {Error} If not authenticated or if the query fails.
 */
export async function getDunningEventsForInvoice(invoiceId: string): Promise<DunningEvent[]> {
  const uid = requireCurrentUser();

  const q = query(
    collection(db, DUNNING_EVENTS_COLLECTION),
    where('userId', '==', uid),
    where('invoiceId', '==', invoiceId),
    orderBy('createdAt', 'asc'),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d): DunningEvent => ({ id: d.id, ...d.data() } as DunningEvent));
}

/**
 * Loads ALL dunning events for the authenticated user in a single round-trip.
 *
 * Uses only `where('userId', '==', uid)` — no `orderBy` — so the single-field
 * auto-index on `userId` is sufficient and no composite index needs to be
 * provisioned.  The caller is responsible for client-side sorting (e.g.
 * by level/createdAt) and grouping (e.g. by invoiceId).
 *
 * Design note: avoids the N+1 pattern of calling `getDunningEventsForInvoice`
 * per visible invoice row.  Mirrors the `getPaymentsForUser` pattern used by
 * `OpenItemsPage.loadData` (paymentsByInvoice Map).
 *
 * @param uid - UID of the authenticated user (must match `auth.currentUser.uid`).
 * @returns Array of all DunningEvent documents owned by this user.  May be empty.
 * @throws {Error} If not authenticated or if the Firestore query fails.
 */
export async function getAllDunningEventsForUser(uid: string): Promise<DunningEvent[]> {
  const currentUid = requireCurrentUser();

  if (uid !== currentUid) {
    throw new Error(
      'dunningApi.getAllDunningEventsForUser: uid must match the authenticated user. ' +
      'Cross-user reads are denied by Firestore Rules.',
    );
  }

  const q = query(
    collection(db, DUNNING_EVENTS_COLLECTION),
    where('userId', '==', uid),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d): DunningEvent => ({ id: d.id, ...d.data() } as DunningEvent));
}

/**
 * Loads ALL dunning deliveries for the authenticated user in a single round-trip.
 *
 * Uses only `where('userId', '==', uid)` — no `orderBy` — so the single-field
 * auto-index on `userId` suffices.  Group the results by `dunningEventId` or
 * `invoiceId` on the client side as needed.
 *
 * @param uid - UID of the authenticated user (must match `auth.currentUser.uid`).
 * @returns Array of all DunningDelivery documents owned by this user.  May be empty.
 * @throws {Error} If not authenticated or if the Firestore query fails.
 */
export async function getAllDunningDeliveriesForUser(uid: string): Promise<DunningDelivery[]> {
  const currentUid = requireCurrentUser();

  if (uid !== currentUid) {
    throw new Error(
      'dunningApi.getAllDunningDeliveriesForUser: uid must match the authenticated user. ' +
      'Cross-user reads are denied by Firestore Rules.',
    );
  }

  const q = query(
    collection(db, DUNNING_DELIVERIES_COLLECTION),
    where('userId', '==', uid),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (d): DunningDelivery => ({ id: d.id, ...d.data() } as DunningDelivery),
  );
}

// ─── dunning_invoice_settings (MA-3) ───────────────────────────────────────────

/**
 * Loads all per-invoice Mahn-Automatik overrides for the authenticated user,
 * keyed by `invoiceId` (== docId, architecture-mahn-automatik.md §6.5).
 *
 * Uses only `where('userId', '==', uid)` — no `orderBy` — so the single-field
 * auto-index on `userId` suffices. An invoice with no entry in the returned
 * Map has no override (gate treats it as `{ excludeFromAutomation: false,
 * snoozeUntilISO: null }`).
 *
 * @param uid - UID of the authenticated user (must match `auth.currentUser.uid`).
 * @returns Map from `invoiceId` to its `DunningInvoiceSettings`. May be empty.
 * @throws {Error} If not authenticated, uid mismatch, or the Firestore query fails.
 */
export async function getDunningInvoiceSettingsForUser(
  uid: string,
): Promise<Map<string, DunningInvoiceSettings>> {
  const currentUid = requireCurrentUser();

  if (uid !== currentUid) {
    throw new Error(
      'dunningApi.getDunningInvoiceSettingsForUser: uid must match the authenticated user. ' +
      'Cross-user reads are denied by Firestore Rules.',
    );
  }

  const q = query(
    collection(db, DUNNING_INVOICE_SETTINGS_COLLECTION),
    where('userId', '==', uid),
  );

  const snapshot = await getDocs(q);
  const result = new Map<string, DunningInvoiceSettings>();
  snapshot.docs.forEach((d) => {
    const data = { id: d.id, ...d.data() } as DunningInvoiceSettings;
    // docId == invoiceId by contract (§6.5); key the Map off the docId so a
    // malformed/legacy `invoiceId` field on the doc can never desync the Map key.
    result.set(d.id, data);
  });
  return result;
}

/**
 * Creates or replaces the per-invoice Mahn-Automatik override
 * (`dunning_invoice_settings/{invoiceId}`, docId == invoiceId).
 *
 * Uses `setDoc` (upsert semantics): the first call creates the document
 * (Rules: `create: if ownsIncoming()`), subsequent calls overwrite it
 * (Rules: `update: if canWriteInTenant(...) && userIdUnchanged()`).
 *
 * Enforces:
 *   - `userId` is always set to `auth.currentUser.uid` (M0 scope).
 *   - `invoiceId` must be a non-empty string (also used as the docId).
 *   - `updatedAt` is set via `serverTimestamp()`.
 *
 * @param input - `invoiceId` (target + docId), `excludeFromAutomation`,
 *                `snoozeUntilISO` (`null` clears an active snooze), optional `note`.
 * @throws {Error} If not authenticated, `invoiceId` is missing, or the write fails.
 */
export async function upsertDunningInvoiceSettings(input: {
  invoiceId: string;
  excludeFromAutomation: boolean;
  snoozeUntilISO: string | null;
  note?: string;
}): Promise<void> {
  const uid = requireCurrentUser();

  if (!input.invoiceId || input.invoiceId.trim() === '') {
    throw new Error(
      'dunningApi.upsertDunningInvoiceSettings: invoiceId must be a non-empty string.',
    );
  }

  const docData: Omit<DunningInvoiceSettings, 'id'> = {
    userId: uid,
    invoiceId: input.invoiceId,
    excludeFromAutomation: input.excludeFromAutomation,
    snoozeUntilISO: input.snoozeUntilISO,
    updatedAt: serverTimestamp() as DunningInvoiceSettings['updatedAt'],
    // `?? undefined` VERBOTEN (Firestore-Write-Crash-Klasse) — omit entirely
    // when the caller did not supply a note.
    ...(input.note !== undefined ? { note: input.note } : {}),
  };

  const docRef = doc(db, DUNNING_INVOICE_SETTINGS_COLLECTION, input.invoiceId);
  await setDoc(docRef, docData);
}

// ─── dunning_level_locks (MA-3, R3/E11) ────────────────────────────────────────

/**
 * Attempts to atomically acquire the dedup guard for `(uid, invoiceId, level)`
 * (architecture-mahn-automatik.md §5.3/§6.7/R3).
 *
 * Implementation contract: `setDoc` on the deterministic docId
 * `${uid}_${invoiceId}_L${level}` in `dunning_level_locks`. Firestore Rules
 * (MA-4) permit ONLY `create` on this collection (`update, delete: if false`)
 * — a `setDoc` on an already-existing docId is therefore rejected server-side
 * with `permission-denied`, which this function treats as the expected
 * `'exists'` outcome rather than an error. This gives atomic create-only
 * semantics WITHOUT a Firestore transaction.
 *
 * Contract for callers (this function itself has NO UI — it only reports
 * `'acquired'` | `'exists'` and never blocks): DunningModal (manual path) calls
 * this BEFORE `POST /dunning/generate`; on `'exists'` it shows its own explicit
 * confirmation dialog ("already generated for this level — dun again as a
 * correction?") and only proceeds if the user confirms (append-only
 * `dunning_events` preserves GoBD correction capability, JURIST J-D) — the
 * manual path is never hard-blocked by this function, the caller decides.
 * DunningAutomationPanel (semi-auto path, MA-6) calls it too; on `'exists'` it
 * skips the row as `SKIPPED_LEVEL_ALREADY_SENT` without asking. Vollautomatik
 * (MA-9) uses the Admin SDK `create()` directly server-side and does NOT call
 * this function.
 *
 * The `trigger` recorded on the lock document is `'manual'` — this function
 * is invoked from both the manual and semi-automatic FE paths, and the field
 * is informational/audit-only (it never gates any decision); callers that
 * need a more precise value can be revisited if that audit precision becomes
 * load-bearing (see changelog.md).
 *
 * @param invoiceId - Firestore document ID of the invoice.
 * @param level - Dunning level being locked (1..3, positive integer).
 * @returns `'acquired'` on first call for this `(uid, invoiceId, level)`,
 *          `'exists'` if the lock was already held (by any of the three paths).
 * @throws {Error} If not authenticated, `invoiceId`/`level` are invalid, or the
 *         write fails for a reason other than `permission-denied`.
 */
export async function acquireDunningLevelLock(
  invoiceId: string,
  level: number,
): Promise<'acquired' | 'exists'> {
  const uid = requireCurrentUser();

  if (!invoiceId || invoiceId.trim() === '') {
    throw new Error(
      'dunningApi.acquireDunningLevelLock: invoiceId must be a non-empty string.',
    );
  }

  if (!Number.isInteger(level) || level < 1) {
    throw new Error(
      `dunningApi.acquireDunningLevelLock: level must be a positive integer (got ${level}).`,
    );
  }

  const lockId = `${uid}_${invoiceId}_L${level}`;
  const docData: Omit<DunningLevelLock, 'id'> = {
    userId: uid,
    invoiceId,
    level,
    trigger: 'manual',
    createdAt: serverTimestamp() as DunningLevelLock['createdAt'],
    createdBy: uid,
  };

  try {
    const docRef = doc(db, DUNNING_LEVEL_LOCKS_COLLECTION, lockId);
    await setDoc(docRef, docData);
    return 'acquired';
  } catch (err) {
    const code = (err as { code?: string } | null | undefined)?.code;
    if (code === 'permission-denied') {
      return 'exists';
    }
    throw err;
  }
}

// ─── sendDunningEmail callable ────────────────────────────────────────────────

interface CallSendDunningEmailInput {
  /** Firestore ID of the `dunning_events` document to send. */
  dunningEventId: string;
  /**
   * Optional recipient e-mail override.  When omitted, the Cloud Function
   * resolves the address from `invoices/{invoiceId}.rawData.buyer.email`.
   */
  recipientEmail?: string;
}

interface CallSendDunningEmailResult {
  success: true;
  /** Firestore ID of the newly created `dunning_deliveries` document. */
  deliveryId: string;
  /** E-mail address the dunning notice was sent to. */
  recipient: string;
}

/**
 * Calls the `sendDunningEmail` Firebase Cloud Function (region: europe-west3).
 *
 * Sends the dunning PDF (identified via `dunning_events.pdfStoragePath`) as
 * an e-mail attachment through Brevo Transactional and writes an append-only
 * `dunning_deliveries` record after confirmed delivery.
 *
 * Requires:
 *   - The `dunning_events` document must have a non-empty `pdfStoragePath`.
 *   - A buyer e-mail must be resolvable from the linked invoice, OR
 *     `recipientEmail` must be supplied.
 *
 * @throws {FirebaseFunctionsError} On auth failure, missing PDF, missing
 *         recipient, Brevo error, or any internal Cloud Function error.
 */
export async function callSendDunningEmail(
  input: CallSendDunningEmailInput,
): Promise<CallSendDunningEmailResult> {
  const callable = httpsCallable<CallSendDunningEmailInput, CallSendDunningEmailResult>(
    functions,
    'sendDunningEmail',
  );
  const result = await callable(input);
  return result.data;
}
